import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { typeLabels } from "@/mocks/movies";
import type { MovieType } from "@/mocks/movies";
import { filterMovies, getMoviesByType, isValidType } from "@/lib/movieUtils";
import MovieGrid from "@/components/feature/MovieGrid";
import FilterPanel, { type CategoryFilters } from "@/pages/category/components/FilterPanel";
import Pagination from "@/pages/category/components/Pagination";

const PAGE_SIZE = 12;

const channelInfo: Record<MovieType, { title: string; desc: string }> = {
  movie: { title: "电影频道", desc: "最新电影一览，涵盖科幻、悬疑、动作、爱情等多元类型" },
  series: { title: "电视剧频道", desc: "热门剧集齐聚，都市、古装、医疗、武侠一网打尽" },
  variety: { title: "综艺频道", desc: "精彩综艺不断档，真人秀、音乐、美食、竞技应有尽有" },
};

const defaultFilters: CategoryFilters = {
  region: "全部",
  year: "全部",
  genre: "全部",
  sort: "latest",
};

export default function CategoryPage() {
  const { type } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const validType: MovieType = isValidType(type) ? type : "movie";
  const info = channelInfo[validType];

  const filters: CategoryFilters = {
    region: searchParams.get("region") || defaultFilters.region,
    year: searchParams.get("year") || defaultFilters.year,
    genre: searchParams.get("genre") || defaultFilters.genre,
    sort: searchParams.get("sort") || defaultFilters.sort,
  };

  useEffect(() => {
    document.title = `${info.title} - 青青草视频`;
    return () => {
      document.title = "青青草视频—高清影视资源在线观看与最新影视推荐";
    };
  }, [info.title]);

  useEffect(() => {
    setPage(1);
  }, [filters.region, filters.year, filters.genre, filters.sort, validType]);

  const filtered = useMemo(
    () => filterMovies(getMoviesByType(validType), filters),
    [validType, filters.region, filters.year, filters.genre, filters.sort]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilterChange = (key: keyof CategoryFilters, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "全部" || (key === "sort" && value === "latest")) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const handleReset = () => setSearchParams(new URLSearchParams());

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5 flex-wrap">
        <Link to="/" className="hover:text-primary-300 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">{typeLabels[validType]}</span>
      </nav>

      <header className="mb-6 md:mb-8">
        <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
          {info.title}
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
          {info.desc}
        </p>
      </header>

      <div className="flex flex-col lg:flex-row gap-6">
        <FilterPanel
          type={validType}
          filters={filters}
          onChange={handleFilterChange}
          onReset={handleReset}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
            <p className="text-sm text-foreground-600">
              共找到 <span className="text-primary-300 font-semibold">{filtered.length}</span> 部影视内容
            </p>
            <p className="text-xs text-foreground-500">
              第 {page} / {totalPages} 页
            </p>
          </div>

          <MovieGrid
            movies={pageItems}
            showViews
            emptyText="暂无符合条件的影视内容"
            columns="grid-cols-2 sm:grid-cols-3 xl:grid-cols-4"
          />

          {filtered.length === 0 && (
            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line" />
                重置筛选条件
              </button>
            </div>
          )}

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>
    </div>
  );
}